import React from 'react';
import toast from 'react-hot-toast';

interface ToastOptions {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
}

// Success toast with consistent styling
export const showSuccess = (message: string, options?: ToastOptions) => {
  return toast.success(message, {
    duration: options?.duration ?? 4000,
    position: options?.position ?? 'top-right',
  });
};

// Error toast with consistent styling
export const showError = (message: string, options?: ToastOptions) => {
  return toast.error(message, {
    duration: options?.duration ?? 5000,
    position: options?.position ?? 'top-right',
  });
};

// Info toast with custom styling
export const showInfo = (message: string, options?: ToastOptions) => {
  return toast(message, {
    duration: options?.duration ?? 4000,
    position: options?.position ?? 'top-right',
    icon: '💡',
    style: {
      background: '#3b82f6',
      color: '#fff',
    },
  });
};

// Warning toast with custom styling
export const showWarning = (message: string, options?: ToastOptions) => {
  return toast(message, {
    duration: options?.duration ?? 4000,
    position: options?.position ?? 'top-right',
    icon: '⚠️',
    style: {
      background: '#f59e0b',
      color: '#fff',
    },
  });
};

// Loading toast that returns a function to update/dismiss
export const showLoading = (message: string, options?: ToastOptions) => {
  const toastId = toast.loading(message, {
    position: options?.position ?? 'top-right',
  });

  return {
    success: (successMessage: string) => {
      toast.success(successMessage, {
        id: toastId,
        duration: options?.duration ?? 4000,
      });
    },
    error: (errorMessage: string) => {
      toast.error(errorMessage, {
        id: toastId,
        duration: options?.duration ?? 5000,
      });
    },
    dismiss: () => {
      toast.dismiss(toastId);
    },
  };
};

// Promise-based toast for async operations
export const showPromise = <T,>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((err: any) => string);
  },
  options?: ToastOptions
) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading,
      success: messages.success,
      error: messages.error,
    },
    {
      position: options?.position ?? 'top-right',
      success: {
        duration: options?.duration ?? 4000,
      },
      error: {
        duration: options?.duration ?? 5000,
      },
    }
  );
};

// Dismiss a specific toast
export const dismissToast = (toastId?: string) => {
  if (toastId) {
    toast.dismiss(toastId);
  } else {
    toast.dismiss();
  }
};

// Custom toast with full control
export const showCustom = (
  content: React.ReactNode,
  options?: ToastOptions & { style?: React.CSSProperties }
) => {
  return toast.custom((t) => {
    if (typeof content === 'string') {
      return <div>{content}</div>;
    }
    return <>{content}</>;
  }, {
    duration: options?.duration ?? 4000,
    position: options?.position ?? 'top-right',
    style: options?.style,
  });
};